import React, { useMemo, useState } from "react";
import {
  Avatar, Box, Button, CircularProgress, Divider, IconButton,
  Typography,
} from "@mui/material";
import { useGetLeadsQuery, useGetLeadQuery,
  useUpdateLeadStatusMutation, useAddLeadCommunicationMutation,
  useConvertLeadMutation } from "../../../Api/attoDeskApi";
import { ILead, ILeadCommunication, LeadStatus } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";
import { useNavigate } from "react-router-dom";
import CalendarView from "../Calendar";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import TextField from "@mui/material/TextField";

import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CloseIcon              from "@mui/icons-material/Close";
import EmailOutlinedIcon      from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon      from "@mui/icons-material/PhoneAndroid";
import SmsOutlinedIcon        from "@mui/icons-material/Sms";
import NoteAltOutlinedIcon    from "@mui/icons-material/NoteAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import RouteOutlinedIcon      from "@mui/icons-material/RouteOutlined";
import TagIcon                from "@mui/icons-material/Tag";
import SendIcon               from "@mui/icons-material/Send";
import OpenInNewIcon          from "@mui/icons-material/OpenInNew";

dayjs.extend(relativeTime);

// ── Design tokens ──────────────────────────────────────────────────────────────
const BORDER = "#e2e8f0";

const PIPELINE: { value: LeadStatus; label: string; color: string; bg: string; border: string }[] = [
  { value: "new",       label: "New",       color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe" },
  { value: "contacted", label: "Contacted", color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
  { value: "qualified", label: "Follow-up", color: "#f97316", bg: "#fff7ed", border: "#fed7aa" },
  { value: "converted", label: "Converted", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  { value: "lost",      label: "Closed",    color: "#64748b", bg: "#f8fafc", border: "#cbd5e1" },
];

const COMM_CONFIG = {
  note:  { label: "Note",  color: "#64748b", bg: "#f8fafc",  icon: <NoteAltOutlinedIcon  sx={{ fontSize: 13 }} /> },
  call:  { label: "Call",  color: "#f59e0b", bg: "#fffbeb",  icon: <PhoneOutlinedIcon    sx={{ fontSize: 13 }} /> },
  email: { label: "Email", color: "#2f3891", bg: "#eef2ff",  icon: <EmailOutlinedIcon    sx={{ fontSize: 13 }} /> },
  sms:   { label: "SMS",   color: "#0ea5e9", bg: "#f0f9ff",  icon: <SmsOutlinedIcon      sx={{ fontSize: 13 }} /> },
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  school: "School Trip", wedding: "Wedding", corporate: "Corporate", other: "Other",
};

// ── Pipeline step ──────────────────────────────────────────────────────────────
const PipelineStep: React.FC<{
  step: typeof PIPELINE[0]; currentStatus: LeadStatus; index: number; total: number; onClick: () => void;
}> = ({ step, currentStatus, index, total, onClick }) => {
  const currentIdx  = PIPELINE.findIndex((p) => p.value === currentStatus);
  const isActive    = step.value === currentStatus;
  const isCompleted = index < currentIdx;
  return (
    <Box sx={{ flex: 1, position: "relative" }}>
      {index < total - 1 && (
        <Box sx={{ position: "absolute", top: 15, left: "50%", width: "100%", height: 2,
          bgcolor: isCompleted ? "#16a34a" : "#e2e8f0", zIndex: 0 }} />
      )}
      <Box onClick={onClick}
        sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.4,
          cursor: "pointer", position: "relative", zIndex: 1,
          "&:hover .step-circle": { transform: "scale(1.1)", boxShadow: `0 0 0 3px ${step.bg}` } }}>
        <Box className="step-circle"
          sx={{ width: 30, height: 30, borderRadius: "50%",
            bgcolor: isActive ? step.color : isCompleted ? "#16a34a" : "#f1f5f9",
            border: `2px solid ${isActive ? step.color : isCompleted ? "#16a34a" : BORDER}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.2s ease", boxShadow: isActive ? `0 0 0 3px ${step.bg}` : "none" }}>
          {isCompleted
            ? <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "#fff" }} />
            : <Typography sx={{ fontSize: 10, fontWeight: 800, color: isActive ? "#fff" : "#94a3b8" }}>{index + 1}</Typography>}
        </Box>
        <Typography sx={{ fontSize: 9, fontWeight: isActive ? 800 : 500, whiteSpace: "nowrap",
          color: isActive ? step.color : isCompleted ? "#16a34a" : "#94a3b8" }}>
          {step.label}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Inline Detail Panel ────────────────────────────────────────────────────────
const DetailPanel: React.FC<{ leadId: number; onClose: () => void }> = ({ leadId, onClose }) => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const [activeAction, setActiveAction] = useState<"note" | "call" | "email" | "sms" | null>(null);
  const [commMsg, setCommMsg]           = useState("");
  const [savingComm, setSavingComm]     = useState(false);
  const [converting, setConverting]     = useState(false);

  const { data, isLoading, refetch } = useGetLeadQuery(leadId);
  const [updateStatus] = useUpdateLeadStatusMutation();
  const [addComm]      = useAddLeadCommunicationMutation();
  const [convertLead]  = useConvertLeadMutation();

  const lead: ILead | null = (data as any)?.data ?? null;

  const handleStatusChange = async (newStatus: LeadStatus) => {
    if (!lead || lead.status === newStatus) return;
    const res: any = await updateStatus({ id: lead.id, status: newStatus });
    if (res?.error || res?.data?.success === false) showErrorMessage("Failed to update status.");
    else { showMessage(`Status → ${PIPELINE.find((p) => p.value === newStatus)?.label}`); refetch(); }
  };

  const handleLogComm = async () => {
    if (!lead || !commMsg.trim() || !activeAction) return;
    setSavingComm(true);
    const res: any = await addComm({ id: lead.id, type: activeAction, message: commMsg.trim() });
    if (res?.error || res?.data?.success === false) showErrorMessage("Failed to log.");
    else { showMessage("Logged."); setCommMsg(""); setActiveAction(null); refetch(); }
    setSavingComm(false);
  };

  const handleConvert = async () => {
    if (!lead) return;
    setConverting(true);
    const res: any = await convertLead(lead.id);
    if (res?.error || res?.data?.success === false) showErrorMessage("Failed to convert.");
    else { showMessage("Converted to customer."); refetch(); }
    setConverting(false);
  };

  const currentPipeline = PIPELINE.find((p) => p.value === lead?.status) || PIPELINE[0];

  return (
    <Box sx={{
      display: "flex", flexDirection: "column", height: "100%",
      animation: "slideInPanel 0.22s ease-out",
      "@keyframes slideInPanel": {
        from: { opacity: 0, transform: "translateX(32px)" },
        to:   { opacity: 1, transform: "translateX(0)" },
      },
    }}>
      {/* Panel header */}
      <Box sx={{
        px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}`,
        display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0,
        bgcolor: "#ffffff", position: "sticky", top: 0, zIndex: 10,
      }}>
        <IconButton size="small" onClick={onClose}
          sx={{ bgcolor: "#f1f5f9", borderRadius: 1.5, "&:hover": { bgcolor: "#e2e8f0" } }}>
          <ArrowBackIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", flex: 1 }}>
          ← Back to Calendar
        </Typography>
        {lead && (
          <IconButton size="small" onClick={() => navigate(`/enquiry/${leadId}`)}
            sx={{ "&:hover": { bgcolor: "#eff6ff" } }}>
            <OpenInNewIcon sx={{ fontSize: 15, color: "#2f3891" }} />
          </IconButton>
        )}
        <IconButton size="small" onClick={onClose} sx={{ "&:hover": { bgcolor: "#fef2f2" } }}>
          <CloseIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
        </IconButton>
      </Box>

      {/* Loading state */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Panel content */}
      {!isLoading && lead && (
        <Box sx={{ overflowY: "auto", flex: 1, p: 0 }}>

          {/* Identity bar */}
          <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${BORDER}`, bgcolor: "#fafbff" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar sx={{ width: 40, height: 40, bgcolor: "#eef2ff", color: "#2f3891",
                fontSize: 16, fontWeight: 800, borderRadius: 2, flexShrink: 0 }}>
                {(lead.contact.firstname[0] || "?").toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>
                    {lead.contact.firstname} {lead.contact.lastname || ""}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, px: 0.75, py: 0.2,
                    bgcolor: "#f1f5f9", borderRadius: 1 }}>
                    <TagIcon sx={{ fontSize: 9, color: "#94a3b8" }} />
                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#2f3891", fontFamily: "monospace" }}>
                      {lead.lead_ref}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
                  <Box sx={{ px: 0.75, py: 0.15, bgcolor: currentPipeline.bg,
                    border: `1px solid ${currentPipeline.border}`, borderRadius: 1 }}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: currentPipeline.color }}>
                      {currentPipeline.label}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                    {dayjs(lead.created_at).fromNow()}
                  </Typography>
                </Box>
              </Box>
            </Box>
            {/* Contact info */}
            <Box sx={{ display: "flex", gap: 2, mt: 1.25, flexWrap: "wrap" }}>
              {lead.contact.email && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <EmailOutlinedIcon sx={{ fontSize: 11, color: "#94a3b8" }} />
                  <Typography sx={{ fontSize: 11, color: "#374151" }} noWrap>{lead.contact.email}</Typography>
                </Box>
              )}
              {lead.contact.phone && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PhoneOutlinedIcon sx={{ fontSize: 11, color: "#94a3b8" }} />
                  <Typography sx={{ fontSize: 11, color: "#374151" }}>{lead.contact.phone}</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Event + message */}
          <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${BORDER}` }}>
            {(lead.contact.event_type || lead.contact.event_date) && (
              <Box sx={{ display: "flex", gap: 1.5, mb: lead.contact.message ? 1.5 : 0, flexWrap: "wrap" }}>
                {lead.contact.event_type && (
                  <Box sx={{ px: 1, py: 0.3, bgcolor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 1.5 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#2563eb" }}>
                      {EVENT_TYPE_LABELS[lead.contact.event_type] ?? lead.contact.event_type}
                    </Typography>
                  </Box>
                )}
                {lead.contact.event_date && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5,
                    px: 1, py: 0.3, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 1.5 }}>
                    <CalendarTodayOutlinedIcon sx={{ fontSize: 10, color: "#16a34a" }} />
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#16a34a" }}>
                      {dayjs(lead.contact.event_date).format("MMM D, YYYY")}
                    </Typography>
                  </Box>
                )}
                {(lead.contact.route_pickup || lead.contact.route_dropoff) && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5,
                    px: 1, py: 0.3, bgcolor: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 1.5 }}>
                    <RouteOutlinedIcon sx={{ fontSize: 10, color: "#f97316" }} />
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#c2410c" }} noWrap>
                      {lead.contact.route_pickup || "?"}{lead.contact.route_dropoff ? ` → ${lead.contact.route_dropoff}` : ""}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {lead.contact.message && (
              <Box sx={{ bgcolor: "#f8fafc", borderRadius: 1.5, border: `1px solid ${BORDER}`, p: 1.5 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8",
                  textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>Message</Typography>
                <Typography sx={{ fontSize: 12, color: "#374151", lineHeight: 1.6 }}>
                  {lead.contact.message}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Pipeline */}
          <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#64748b",
              textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>Pipeline</Typography>
            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
              {PIPELINE.map((step, i) => (
                <PipelineStep key={step.value} step={step} currentStatus={lead.status}
                  index={i} total={PIPELINE.length} onClick={() => handleStatusChange(step.value)} />
              ))}
            </Box>
          </Box>

          {/* Quick actions */}
          <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#64748b",
              textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>Quick Actions</Typography>
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              {(["note", "call", "email", "sms"] as const).map((type) => {
                const cfg = COMM_CONFIG[type];
                const isActive = activeAction === type;
                return (
                  <Button key={type} size="small"
                    startIcon={React.cloneElement(cfg.icon as React.ReactElement, { sx: { fontSize: 12 } })}
                    onClick={() => setActiveAction(isActive ? null : type)}
                    variant={isActive ? "contained" : "outlined"}
                    sx={{
                      textTransform: "none", fontSize: 11, fontWeight: 600,
                      borderRadius: 1.5, px: 1.25, height: 30,
                      bgcolor: isActive ? cfg.color : "transparent",
                      borderColor: isActive ? cfg.color : BORDER,
                      color: isActive ? "#ffffff" : cfg.color,
                      "&:hover": { bgcolor: isActive ? cfg.color : cfg.bg, borderColor: cfg.color },
                    }}>
                    {cfg.label}
                  </Button>
                );
              })}
            </Box>
            {activeAction && (
              <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-end", mt: 1 }}>
                <TextField fullWidth multiline rows={2} size="small"
                  value={commMsg} onChange={(e) => setCommMsg(e.target.value)}
                  placeholder={`Log a ${COMM_CONFIG[activeAction].label.toLowerCase()}…`}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 12 } }} />
                <Button size="small" variant="contained" disabled={!commMsg.trim() || savingComm}
                  onClick={handleLogComm}
                  startIcon={savingComm ? <CircularProgress size={11} sx={{ color: "#fff" }} /> : <SendIcon sx={{ fontSize: 13 }} />}
                  sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, height: 32, px: 1.5, flexShrink: 0,
                    bgcolor: COMM_CONFIG[activeAction].color,
                    "&:hover": { bgcolor: COMM_CONFIG[activeAction].color, filter: "brightness(0.9)" } }}>
                  Log
                </Button>
              </Box>
            )}
          </Box>

          {/* Activity history */}
          <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#64748b",
              textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.25 }}>Activity History</Typography>
            {(() => {
              const all = [
                ...(lead.communications || []).map((c) => ({ ...c, _type: "comm" as const })),
                ...(lead.statusHistory  || []).map((h) => ({ ...h, _type: "status" as const })),
              ].sort((a, b) => dayjs(b.created_at).diff(dayjs(a.created_at)));

              if (!all.length) return (
                <Typography sx={{ fontSize: 12, color: "#94a3b8", textAlign: "center", py: 2 }}>
                  No activity yet.
                </Typography>
              );

              return all.map((ev, i) => {
                if (ev._type === "comm") {
                  const c = ev as ILeadCommunication & { _type: "comm" };
                  const cfg = COMM_CONFIG[c.type];
                  return (
                    <Box key={`c-${i}`} sx={{ display: "flex", gap: 1.5, py: 1.25,
                      borderBottom: i < all.length - 1 ? `1px solid #f8fafc` : "none" }}>
                      <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: cfg.bg,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, color: cfg.color, mt: 0.1 }}>
                        {cfg.icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
                          <Box sx={{ px: 0.6, py: 0.1, bgcolor: cfg.bg, borderRadius: 0.75 }}>
                            <Typography sx={{ fontSize: 9, fontWeight: 700, color: cfg.color }}>{cfg.label}</Typography>
                          </Box>
                          <Typography sx={{ fontSize: 10, color: "#94a3b8", ml: "auto", flexShrink: 0 }}>
                            {dayjs(c.created_at).fromNow()}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 11, color: "#374151", lineHeight: 1.5 }}>{c.message}</Typography>
                      </Box>
                    </Box>
                  );
                }
                const h = ev as any;
                const nxt = PIPELINE.find((p) => p.value === h.new_status);
                return (
                  <Box key={`h-${i}`} sx={{ display: "flex", gap: 1.5, py: 1.25,
                    borderBottom: i < all.length - 1 ? `1px solid #f8fafc` : "none" }}>
                    <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: nxt?.bg || "#f8fafc",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: nxt?.color || "#94a3b8" }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Typography sx={{ fontSize: 11, color: "#374151" }}>
                          {h.old_status
                            ? <>{PIPELINE.find((p) => p.value === h.old_status)?.label ?? h.old_status}
                                {" → "}<strong style={{ color: nxt?.color }}>{nxt?.label ?? h.new_status}</strong></>
                            : <>Created as <strong style={{ color: nxt?.color }}>{nxt?.label}</strong></>}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: "#94a3b8", ml: "auto", flexShrink: 0 }}>
                          {dayjs(h.created_at).fromNow()}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              });
            })()}
          </Box>

          {/* Convert actions */}
          <Box sx={{ px: 2.5, py: 2 }}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {lead.status !== "converted" && (
                <Button size="small" variant="outlined" disabled={converting}
                  startIcon={converting ? <CircularProgress size={12} /> : <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                  onClick={handleConvert}
                  sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11,
                    borderColor: "#16a34a", color: "#16a34a",
                    "&:hover": { bgcolor: "#f0fdf4", borderColor: "#15803d" } }}>
                  Convert to Customer
                </Button>
              )}
              {lead.customer && (
                <Box sx={{ px: 1.25, py: 0.5, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 1.5 }}>
                  <Typography sx={{ fontSize: 11, color: "#16a34a", fontWeight: 700 }}>
                    ✓ {lead.customer.name}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ── Main Calendar Page ─────────────────────────────────────────────────────────
const EnquiryCalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);

  const { data, isLoading } = useGetLeadsQuery({});
  const leads: ILead[] = useMemo(() => {
    if (!data || !Array.isArray((data as any).data)) return [];
    return (data as any).data as ILead[];
  }, [data]);

  return (
    <Box sx={{
      display: "flex", height: "100vh", overflow: "hidden", bgcolor: "#f8fafc",
    }}>
      {/* ── LEFT: Calendar ── */}
      <Box sx={{
        flex: 1, minWidth: 0, overflowY: "auto", p: 3,
        transition: "opacity 0.2s ease",
        opacity: selectedLeadId ? 0.55 : 1,
        pointerEvents: selectedLeadId ? "none" : "auto",
      }}>
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>
            Enquiry Calendar
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.25 }}>
            Click an enquiry to view details — no page navigation
          </Typography>
        </Box>
        <CalendarView
          leads={leads}
          isLoading={isLoading}
          onLeadClick={(id) => navigate(`/enquiry/${id}`)}
        />
      </Box>

      {/* ── Overlay (tap to close panel on mobile) ── */}
      {selectedLeadId && (
        <Box
          onClick={() => setSelectedLeadId(null)}
          sx={{
            display: { xs: "block", md: "none" },
            position: "fixed", inset: 0, bgcolor: "rgba(0,0,0,0.3)", zIndex: 99,
          }}
        />
      )}

      {/* ── RIGHT: Detail panel ── */}
      <Box sx={{
        width: selectedLeadId ? { xs: "100%", sm: 440 } : 0,
        flexShrink: 0,
        overflow: "hidden",
        transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
        borderLeft: selectedLeadId ? `1px solid ${BORDER}` : "none",
        bgcolor: "#ffffff",
        position: { xs: "fixed", md: "relative" },
        right: 0, top: 0, bottom: 0,
        zIndex: { xs: 100, md: 1 },
        boxShadow: { xs: "-4px 0 24px rgba(0,0,0,0.12)", md: "none" },
      }}>
        {selectedLeadId && (
          <DetailPanel
            leadId={selectedLeadId}
            onClose={() => setSelectedLeadId(null)}
          />
        )}
      </Box>
    </Box>
  );
};

export default EnquiryCalendarPage;
